(() => {
  'use strict';

  // This model chooses merchandise only. The cart runtime owns price validation,
  // Shopify mutations, discount application, rendering, and localized money.
  const canonicalBites = new Map([
    [54091373216049, '8OZ-2PK-MANGO-BITES'],
    [54091393007921, '8OZ-2PK-PEACH'],
    [54091386421553, '8OZ-2PK-STRAWBERRYBITE'],
    [54091379671345, '8OZ-2PK-WATERMELON'],
  ]);
  const fixedFamilies = new Set(['variety', 'mega', 'stockup']);
  const positiveInteger = (value) => {
    if (typeof value !== 'number' && typeof value !== 'string') return 0;
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : 0;
  };

  const validProducts = (products) => {
    const seen = new Set();
    return (Array.isArray(products) ? products : []).flatMap((product, index) => {
      if (!product || product.available !== true) return [];
      const id = positiveInteger(product.id);
      const price = positiveInteger(product.price);
      const bagCount = positiveInteger(product.bagCount);
      const sku = typeof product.sku === 'string' ? product.sku.trim() : '';
      const title = typeof product.title === 'string' ? product.title.trim() : '';
      if (!id || !price || !bagCount || !sku || !title || seen.has(id)) return [];
      if (product.kind !== 'bites' && product.kind !== 'small') return [];
      if (product.kind === 'bites' && (canonicalBites.get(id) !== sku || bagCount !== 2)) return [];
      if (product.kind === 'small' && canonicalBites.has(id)) return [];
      seen.add(id);
      return [{
        product: { ...product, id, price, bagCount, sku, title },
        index,
      }];
    });
  };

  const cartState = (cart) => {
    const quantities = new Map();
    let hasFixedBundle = false;
    (Array.isArray(cart?.items) ? cart.items : []).forEach((item) => {
      const quantity = positiveInteger(item?.quantity);
      if (!quantity) return;
      const id = positiveInteger(item.variant_id) || positiveInteger(item.id);
      if (id) quantities.set(id, (quantities.get(id) || 0) + quantity);
      const properties = item.properties || {};
      const family = String(properties._ccv1_offer_family || properties.ccv1_offer_family || '').trim().toLowerCase();
      if (fixedFamilies.has(family)) hasFixedBundle = true;
    });
    return { quantities, hasFixedBundle };
  };

  const choose = (candidates, quantities) => candidates.slice().sort((a, b) => (
    (quantities.get(a.product.id) || 0) - (quantities.get(b.product.id) || 0)
      || a.index - b.index
  ))[0]?.product || null;

  const tierFor = (product, pricing, hasFixedBundle) => {
    if (!product || product.kind !== 'bites' || hasFixedBundle || pricing?.nextEligible !== true) return null;
    const increment = positiveInteger(pricing.increment);
    const saving = positiveInteger(pricing.saving);
    const targetPairs = positiveInteger(pricing.targetPairs);
    const discountCode = typeof pricing.discountCode === 'string' ? pricing.discountCode.trim() : '';
    // Fail back to the catalog price if the trusted runtime did not provide a
    // complete, internally consistent next-pair quote.
    if (!increment || !saving || increment + saving !== product.price || targetPairs < 2 || targetPairs > 6 || !discountCode) return null;
    return { increment, saving, targetPairs, discountCode };
  };

  const build = ({ cart, products, pricing, preferSmaller = false } = {}) => {
    const catalog = validProducts(products);
    const { quantities, hasFixedBundle } = cartState(cart);
    const bites = choose(catalog.filter((entry) => entry.product.kind === 'bites'), quantities);
    const smallProducts = catalog.filter((entry) => entry.product.kind === 'small');
    const small = choose(smallProducts, quantities);
    const primary = hasFixedBundle ? small : (bites || small);
    if (!primary) return null;

    const primaryTier = tierFor(primary, pricing, hasFixedBundle);
    const primaryPrice = primaryTier?.increment || primary.price;
    const smaller = primary.kind === 'bites'
      ? choose(smallProducts.filter((entry) => entry.product.price < primaryPrice), quantities)
      : null;
    const isDownsell = preferSmaller === true && smaller !== null;
    const product = isDownsell ? smaller : primary;
    const tier = isDownsell ? null : primaryTier;

    return {
      product,
      incrementalPrice: tier?.increment || product.price,
      saving: tier?.saving || 0,
      targetPairs: tier?.targetPairs || 0,
      discountCode: tier?.discountCode || '',
      isTierOffer: Boolean(tier),
      isDownsell,
      canDownsell: smaller !== null,
      hasFixedBundle,
      title: isDownsell
        ? 'A little extra'
        : (tier ? `Make it ${tier.targetPairs * 2} bags` : 'Try another flavor'),
    };
  };

  window.Ccv1CartOfferModel = Object.freeze({ build });
})();
